'use client';
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import Button from '@/components/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faSun, faMoon, faUserCircle, faBars, faStar } from '@fortawesome/free-solid-svg-icons';
import { useTranslations } from '@/utils/translations';
import MadrielLogo from '@/components/ui/MadrielLogo';

interface HeaderProps {
  className?: string;
}

type ModalType = 'login' | 'menu' | 'register' | 'forgotPassword' | 'accountLogin' | null;

interface FormData {
  loginEmail: string;
  loginPassword: string;
  firstName: string;
  email: string;
  password: string;
  confirmPassword: string;
  forgotEmail: string;
}

export interface HeaderRef {
  openRegistrationModal: () => void;
  openForgotPasswordModal: () => void;
}

const Header = forwardRef<HeaderRef, HeaderProps>(({ className }, ref) => {
  const router = useRouter();
  const { t } = useTranslations('cz');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isUserRegistered, setIsUserRegistered] = useState(false);
  const [isUserSubscribed, setIsUserSubscribed] = useState(false);
  const [showForgotPasswordConfirmation, setShowForgotPasswordConfirmation] = useState(false);
  const [showRegistrationConfirmation, setShowRegistrationConfirmation] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    loginEmail: '',
    loginPassword: '',
    firstName: '',
    email: '',
    password: '',
    confirmPassword: '',
    forgotEmail: ''
  });

  const menuRef = useRef<HTMLDivElement>(null);
  const registrationModalRef = useRef<HTMLDivElement>(null);

  // Expose openRegistrationModal function to parent components
  useImperativeHandle(ref, () => ({
    openRegistrationModal: () => {
      setActiveModal('register');
    },
    openForgotPasswordModal: () => {
      setActiveModal('forgotPassword');
    }
  }));

  // Initialize theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const shouldBeDark = savedTheme === 'dark' || !savedTheme && prefersDark;

    setIsDarkMode(shouldBeDark);
    if (shouldBeDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Check subscription status
    const subscribed = localStorage.getItem('userSubscribed') === 'true';
    setIsUserSubscribed(subscribed);
  }, []);

  // Click outside detection for menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Click outside detection for registration modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (registrationModalRef.current && !registrationModalRef.current.contains(event.target as Node)) {
        if (activeModal === 'register') {
          setActiveModal(null);
        }
      }
    };

    if (activeModal === 'register') {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeModal]);

  // Toggle theme function
  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);

    if (newTheme) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogin = () => {
    // Handle login logic
    setIsUserRegistered(true);
    setActiveModal(null);
  };

  const handleRegister = () => {
    // Handle registration logic
    setShowRegistrationConfirmation(true);
  };

  const handleForgotPassword = () => {
    // Handle forgot password logic
    setShowForgotPasswordConfirmation(true);
  };

  const handleLogout = () => {
    // Handle logout logic
    setIsUserRegistered(false);
    setIsUserSubscribed(false);
    localStorage.removeItem('userAuth');
    localStorage.removeItem('userSubscribed');
    setActiveModal(null);
  };

  const closeModal = () => {
    setActiveModal(null);
    setShowForgotPasswordConfirmation(false);
    setShowRegistrationConfirmation(false);
  };

  return (
    <header className={`fixed top-0 left-[10px] right-[10px] w-auto max-w-[1580px] mx-auto bg-header-background opacity-100 z-header rounded-b-[25px] ${className || ''}`}>
      <div className="w-full max-w-[1580px] mx-auto px-[8px] sm:px-2 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[55px]">

          {/* Logo Madriel */}
          <div className="flex items-center">
            <button
              onClick={() => window.location.href = '/'}
              className="p-2 hover:opacity-80 transition-all duration-300 ease-in-out hover:scale-110 active:scale-95 relative z-header-logo"
              aria-label={t('header.accessibility.home_button_aria')}>
              <MadrielLogo
                className="w-auto h-8 sm:h-8 md:h-10"
              />
            </button>
          </div>

          {/* Center - Empty space for logo area */}
          <div className="flex-1 flex justify-center">
            <div className="w-full max-w-[1580px] flex justify-center items-center h-full">
              {/* Logo removed - header now displays background color only */}
            </div>
          </div>

          {/* Right side - User and Menu Icons */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveModal('login')}
              className="p-2 hover:opacity-80 transition-all duration-300 ease-in-out hover:scale-110 active:scale-95 relative"
              aria-label={t('header.accessibility.user_button_aria')}>

              <FontAwesomeIcon
                icon={faUserCircle}
                className={`w-6 h-6 ${isUserRegistered ? 'text-green-700' : 'text-[#4C4C4C]'}`} />
              
              {/* FontAwesome Star Icon for Subscribed Users */}
              {isUserRegistered && isUserSubscribed && (
                <FontAwesomeIcon
                  icon={faStar}
                  className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 text-yellow-400 pl-4 pt-1.5"
                />
              )}

            </button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 hover:opacity-80 transition-all duration-300 ease-in-out hover:scale-110 active:scale-95"
              aria-label={t('header.accessibility.menu_button_aria')}>

              <FontAwesomeIcon
                icon={faBars}
                className="w-6 h-6 text-[#4C4C4C]" />

            </button>
          </div>

        </div>

        {/* Login Modal */}
        {activeModal === 'login' &&
        <div className="absolute top-0 right-0 mt-0 w-[260px] animate-slideDown z-dropdown">
            <div className="flex flex-col justify-start items-center w-full bg-header-background rounded-b-[16px] pt-[65px] pb-[18px] px-[10px] gap-[10px] overflow-y-auto">
              <div className="flex flex-col justify-start items-center w-full mb-[16px]">
                <button
                onClick={closeModal}
                className="absolute top-3 right-3 text-[30px] font-inter font-medium leading-[20px] text-center text-[#555151] hover:opacity-70 transition-all duration-300 ease-in-out hover:scale-110 active:scale-95">

                    <FontAwesomeIcon icon={faXmark} />
                </button>

                {/* Show only logout button if user is logged in */}
                {isUserRegistered ?
              <>
                    <div className="flex items-center justify-center w-full mt-[30px]">
                      <Button
                    text={t('header.login_modal.logout_button')}
                    onClick={handleLogout}
                    className="bg-[#77C0BB] text-[#444444] font-inter font-medium text-[16px] leading-[30px] rounded-[24px] px-[34px] py-[8px] flex items-center gap-[8px] mx-0 pr-9 transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-lg active:scale-95">
                        <span>{t('header.login_modal.logout_button')}</span>
                        <Image
                      src="/images/img_vector_gray_800.svg"
                      alt={t('header.accessibility.arrow_alt')}
                      width={22}
                      height={14} />
                      </Button>
                    </div>
                  </> :

              <>
                    <div className="relative w-full h-[56px] flex justify-center items-center mt-[22px]">
                      <h1 className="text-[36px] font-inter font-bold leading-[39px] text-center text-[#4C4C4C]">
                        {t('header.login_modal.title')}
                      </h1>
                    </div>

                    <div className="flex flex-col justify-start items-center w-full gap-[20px] mt-[30px]">
                      <input
                    type="email"
                    placeholder={t('header.login_modal.email_placeholder')}
                    value={formData.loginEmail}
                    onChange={(e) => handleInputChange('loginEmail', e.target.value)}
                    className="w-full py-[6px] px-[34px] border border-[#ababab] rounded-[6px] bg-[#dadada] text-[16px] font-inter font-medium leading-[20px] text-[#717171] ml-0 pl-[15px]" />


                      <input
                    type="password"
                    placeholder={t('header.login_modal.password_placeholder')}
                    value={formData.loginPassword}
                    onChange={(e) => handleInputChange('loginPassword', e.target.value)}
                    className="w-full py-[6px] px-[34px] border border-[#ababab] rounded-[6px] bg-[#dadada] text-[16px] font-inter font-medium leading-[20px] text-[#717171] pl-[15px]" />


                      <div className="flex items-center justify-start w-full mx-[12px] mt-[18px] pr-5 pl-[30px]">
                        <Button
                      text={t('header.login_modal.login_button')}
                      onClick={handleLogin}
                      className="bg-[#77C0BB] text-[#444444] font-inter font-medium text-[16px] leading-[30px] rounded-[24px] px-[34px] py-[8px] flex items-center gap-[8px] mx-0 pr-9">
                          <span>{t('header.login_modal.login_button')}</span>
                          <Image
                        src="/images/img_vector_gray_800.svg"
                        alt={t('header.accessibility.arrow_alt')}
                        width={22}
                        height={14} />
                        </Button>
                      </div>

                      <div className="flex flex-col justify-start items-center w-full border-t border-[#454545] px-[30px] mt-[30px]">
                        <p className="text-[16px] font-inter font-medium leading-[24px] text-center text-[#555151] mt-[22px] -mb-px">
                          {t('header.login_modal.no_account_text')}
                        </p>
                        <button
                      onClick={() => setActiveModal('register')}
                      className="text-[16px] font-inter font-semibold leading-[24px] text-center text-[#b71b90] underline">
                          {t('header.login_modal.create_account_button')}
                        </button>
                        <button
                      onClick={() => setActiveModal('forgotPassword')}
                      className="text-[16px] font-inter font-semibold leading-[24px] text-center text-[#717171] underline pt-2.5">
                          {t('header.login_modal.forgot_password_button')}
                        </button>
                      </div>
                    </div>
                  </>
              }
              </div>
            </div>
          </div>
        }

        {/* Account Login Modal Centered */}
        {activeModal === 'accountLogin' &&
        <div className="fixed inset-0 flex items-center justify-center z-modal">
            <div className="absolute inset-0 bg-black bg-opacity-30 z-modal-overlay" onClick={closeModal}></div>
            <div className="relative flex flex-col justify-start items-center w-[360px] bg-[#e3e3e3] rounded-[16px] p-[12px] overflow-y-auto pl-[25px] pr-[25px] pt-[42px] pb-[35px] z-modal">
              <button
              onClick={closeModal}
              className="absolute top-2 right-2 text-[36px] font-inter font-medium leading-[20px] text-center text-[#555151]">
                <FontAwesomeIcon icon={faXmark} />
              </button>

              <div className="flex flex-col justify-start items-center w-full mb-[16px]">
                {/* Informational Text */}
                <div className="w-full mb-[20px]">
                  <p className="text-[16px] font-inter font-medium leading-[22px] text-center text-[#555151]">
                    {t('header.account_login_modal.info_text')}
                  </p>
                </div>

                <div className="relative w-full h-[56px] flex justify-center items-center">
                  <h1 className="text-[30px] font-inter font-bold leading-[39px] text-center text-[#989898]">
                    {t('header.account_login_modal.title')}
                  </h1>
                </div>

                <div className="flex flex-col justify-start items-center w-full gap-[20px] mt-[30px]">
                  <input
                  type="email"
                  placeholder={t('header.account_login_modal.email_placeholder')}
                  value={formData.loginEmail}
                  onChange={(e) => handleInputChange('loginEmail', e.target.value)}
                  className="w-full py-[8px] px-[34px] border border-[#ababab] rounded-[6px] bg-[#dadada] text-[16px] font-inter font-medium leading-[20px] text-[#717171]" />

                  <input
                  type="password"
                  placeholder={t('header.account_login_modal.password_placeholder')}
                  value={formData.loginPassword}
                  onChange={(e) => handleInputChange('loginPassword', e.target.value)}
                  className="w-full py-[8px] px-[34px] border border-[#ababab] rounded-[6px] bg-[#dadada] text-[16px] font-inter font-medium leading-[20px] text-[#717171]" />

                  <div className="flex items-center justify-start w-full mx-[12px] mt-[18px] pl-[22px] pr-5">
                    <Button
                    text={t('header.account_login_modal.login_button')}
                    onClick={handleLogin}
                    className="bg-[#77C0BB] text-[#444444] font-inter font-medium text-[16px] leading-[30px] rounded-[24px] px-[34px] py-[8px] flex items-center gap-[8px] mx-0 pr-9">
                      <span>{t('header.account_login_modal.login_button')}</span>
                      <Image
                      src="/images/img_vector_gray_800.svg"
                      alt={t('header.accessibility.arrow_alt')}
                      width={22}
                      height={14} />
                    </Button>
                  </div>

                  <div className="flex flex-col justify-start items-center w-full border-t border-[#454545] px-[56px] mt-[30px]">
                    <p className="text-[16px] font-inter font-medium leading-[24px] text-center text-[#555151] mt-[22px] -mb-px">
                      {t('header.account_login_modal.no_account_text')}
                    </p>
                    <button
                    onClick={() => setActiveModal('register')}
                    className="text-[16px] font-inter font-semibold leading-[24px] text-center text-[#b71b90] underline">
                      {t('header.account_login_modal.create_account_button')}
                    </button>
                    <button
                    onClick={() => setActiveModal('forgotPassword')}
                    className="text-[16px] font-inter font-semibold leading-[24px] text-center text-[#717171] underline pt-2.5">
                      {t('header.account_login_modal.forgot_password_button')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        }

        {/* Registration Modal Center */}
        {activeModal === 'register' &&
        <div className="fixed inset-0 flex items-center justify-center z-modal">
            <div className="absolute inset-0 bg-black bg-opacity-30 z-modal-overlay" onClick={closeModal}></div>
            <div ref={registrationModalRef} className="relative flex flex-col justify-start items-center w-[360px] bg-white rounded-[16px] p-[12px] overflow-y-auto pl-[25px] pr-[25px] z-modal">
              <div className="flex flex-col justify-start items-center w-full gap-[20px] mb-[16px]">
                <div className="relative w-full h-[56px] flex justify-center items-center">
                  <h1 className="text-[30px] font-inter font-bold leading-[39px] text-center text-[#989898]">
                    {t('header.register_modal.title')}
                  </h1>
                  <button
                  onClick={closeModal}
                  className="absolute top-0 right-0 text-[36px] font-inter font-medium leading-[20px] text-center text-[#555151]">
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </div>

                <div className="flex flex-col justify-start items-center w-full gap-[20px]">
                  {!showRegistrationConfirmation ?
                <>
                      <input
                    type="text"
                    placeholder={t('header.register_modal.first_name_placeholder')}
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="w-full py-[8px] px-[20px] border border-[#ababab] rounded-[6px] bg-[#dadada] text-[16px] font-inter font-semibold leading-[20px] text-left text-[#717171]" />


                      <input
                    type="email"
                    placeholder={t('header.register_modal.email_placeholder')}
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full py-[8px] px-[20px] border border-[#ababab] rounded-[6px] bg-[#dadada] text-[16px] font-inter font-medium leading-[20px] text-left text-[#717171]" />


                      <input
                    type="password"
                    placeholder={t('header.register_modal.password_placeholder')}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className="w-full py-[8px] px-[20px] border border-[#ababab] rounded-[6px] bg-[#dadada] text-[16px] font-inter font-medium leading-[20px] text-left text-[#717171]" />


                      <input
                    type="password"
                    placeholder={t('header.register_modal.confirm_password_placeholder')}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className="w-full py-[8px] px-[20px] border border-[#ababab] rounded-[6px] bg-[#dadada] text-[16px] font-inter font-medium leading-[20px] text-left text-[#717171]" />


                      <div className="flex items-center justify-center w-full mx-[80px]">
                        <Button
                      text={t('header.register_modal.complete_registration_button')}
                      onClick={handleRegister}
                      className="bg-[#77C0BB] text-[#444444] font-inter font-medium text-[16px] leading-[30px] rounded-[24px] px-[34px] py-[8px] flex items-center gap-[4px] mx-0 pr-9">
                          <span>{t('header.register_modal.complete_registration_button')}</span>
                          <Image
                        src="/images/img_vector_gray_800.svg"
                        alt={t('header.accessibility.arrow_alt')}
                        width={22}
                        height={14} />
                        </Button>
                      </div>
                    </> :

                <p className="text-[16px] font-inter font-medium leading-[24px] text-center text-[#555151] px-[10px] py-[20px]">
                      {t('header.register_modal.confirmation_message')}
                    </p>
                }
                </div>

                <div className="flex justify-start items-end w-full border-t border-[#454545]">
                  <p className="text-[16px] font-inter font-medium leading-[23px] text-center text-[#d70f0f] mt-[20px] w-full">
                    {t('header.register_modal.benefits_text')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        }

        {/* Forgot Password Modal Center */}
        {activeModal === 'forgotPassword' &&
        <div className="fixed inset-0 flex items-center justify-center z-modal">
            <div className="absolute inset-0 bg-black bg-opacity-30 z-modal-overlay" onClick={closeModal}></div>
            <div className="relative flex flex-col justify-start items-center w-[360px] bg-white rounded-[16px] p-[12px] overflow-y-auto pl-[25px] pr-[25px] mt-0 pt-[42px] pb-[35px] z-modal">
              <div className="flex flex-col justify-start items-center w-full gap-[20px] mb-[16px]">
                  <button
                onClick={closeModal}
                className="absolute top-2 right-2 text-[36px] font-inter font-medium leading-[20px] text-center text-[#555151]">
                  <FontAwesomeIcon icon={faXmark} />
                  </button>
                <div className="relative w-full h-[56px] flex justify-center items-center">
                  <h1 className="text-[30px] font-inter font-bold leading-[35px] text-center text-[#989898] mt-1.5 pt-0">
                    {t('header.forgot_password_modal.title')}
                  </h1>

                </div>

                <div className="flex flex-col justify-start items-center w-full gap-[20px]">
                  {!showForgotPasswordConfirmation ?
                <>
                      <input
                    type="email"
                    placeholder={t('header.forgot_password_modal.email_placeholder')}
                    value={formData.forgotEmail}
                    onChange={(e) => handleInputChange('forgotEmail', e.target.value)}
                    className="w-full py-[8px] px-[20px] border border-[#ababab] rounded-[6px] bg-[#dadada] text-[16px] font-inter font-semibold leading-[20px] text-left text-[#717171]" />


                      <div className="flex items-center justify-center w-full mx-[80px]">
                        <Button
                      text={t('header.forgot_password_modal.send_button')}
                      onClick={handleForgotPassword}
                      className="bg-[#77C0BB] text-[#444444] font-inter font-medium text-[16px] leading-[30px] rounded-[24px] px-[34px] py-[8px] flex items-center gap-[4px] mx-0 pr-9">
                          <span>{t('header.forgot_password_modal.send_button')}</span>
                          <Image
                        src="/images/img_vector_gray_800.svg"
                        alt={t('header.accessibility.arrow_alt')}
                        width={22}
                        height={14} />
                        </Button>
                      </div>
                    </> :

                <p className="text-[16px] font-inter font-medium leading-[24px] text-center text-[#555151] px-[10px] py-[20px]">
                      {t('header.forgot_password_modal.confirmation_message')}
                    </p>
                }
                </div>
              </div>
            </div>
          </div>
        }

        {/* Menu Overlay */}
        {isMenuOpen &&
        <div ref={menuRef} className="absolute top-0 right-[0px] w-[260px] animate-slideDown z-dropdown">
            <div className="flex flex-col justify-start items-center w-full bg-header-background rounded-b-[16px] pt-[65px] pb-[18px] px-[8px] gap-[8px] overflow-y-auto">
              {/* Hamburger icon on overlay */}
              <button
              onClick={() => setIsMenuOpen(false)}
              className="absolute top-4 right-4 text-[30px] font-inter font-medium leading-[20px] text-center text-[#555151]">

                    <FontAwesomeIcon icon={faXmark} />
                  </button>
              
              <div className="flex flex-col justify-start items-center w-full gap-[10px]">

                <button
                className="w-full bg-[#FFFFFF] bg-opacity-20 text-[#4C4C4C] text-[18px] font-inter font-medium leading-[25px] rounded-[10px] py-[10px] px-[34px] hover:bg-[#c9c9c9] transition-colors duration-200"
                onClick={() => {
                  setIsMenuOpen(false);
                  router.push('/user-account-dashboard');
                }}>

                  {t('header.menu.my_account')}
                </button>

                <button
                className="w-full bg-[#FFFFFF] bg-opacity-20 text-[#4C4C4C] text-[18px] font-inter font-medium leading-[25px] rounded-[10px] py-[10px] px-[34px] hover:bg-[#c9c9c9] transition-colors duration-200"
                onClick={() => {
                  setIsMenuOpen(false);
                  router.push('/my-predictions');
                }}>

                  {t('header.menu.my_predictions')}
                </button>

                <button
                className="w-full bg-[#FFFFFF] bg-opacity-20 text-[#4C4C4C] text-[18px] font-inter font-medium leading-[25px] rounded-[10px] py-[10px] px-[34px] hover:bg-[#c9c9c9] transition-colors duration-200"
                onClick={() => {
                  window.location.href = '/cards-gallery';
                  setIsMenuOpen(false);
                }}>

                  {t('header.menu.cards_overview')}
                </button>

                <button
                className="w-full bg-[#FFFFFF] bg-opacity-20 text-[#4C4C4C] text-[18px] font-inter font-medium leading-[25px] rounded-[10px] py-[10px] px-[34px] hover:bg-[#c9c9c9] transition-colors duration-200"
                onClick={() => {
                  window.location.href = '/about-madriel';
                  setIsMenuOpen(false);
                }}>

                  {t('header.menu.about_madriel')}
                </button>

                {/* Subscription Button - Only visible to logged-in users */}
                {isUserRegistered &&
              <button
                className="w-full bg-[#FFFFFF] bg-opacity-20 text-[#4C4C4C] text-[18px] font-inter font-medium leading-[25px] rounded-[10px] py-[10px] px-[34px] hover:bg-[#c9c9c9] transition-colors duration-200"
                onClick={() => {
                  window.location.href = '/subscription-plans-page';
                  setIsMenuOpen(false);
                }}>

                    {t('header.menu.subscription')}
                  </button>
              }

                {/* Registration Benefits Button - Only visible to non-logged-in users */}
                {!isUserRegistered &&
              <button
                className="w-full bg-[#FFFFFF] bg-opacity-20 text-[#4C4C4C] text-[18px] font-inter font-medium leading-[25px] rounded-[10px] py-[10px] px-[34px] hover:bg-[#c9c9c9] transition-colors duration-200"
                onClick={() => {
                  window.location.href = '/registration-benefits-page';
                  setIsMenuOpen(false);
                }}>

                    {t('header.menu.registration_benefits')}
                  </button>
              }

                {/* Theme Toggle Button */}
                <button
                className="w-full bg-[#FFFFFF] bg-opacity-20 text-[#4C4C4C] text-[18px] font-inter font-medium leading-[25px] rounded-[10px] py-[10px] px-[34px] hover:bg-[#c9c9c9] transition-colors duration-200 flex items-center justify-center gap-[10px]"
                onClick={() => {
                  toggleTheme();
                  setIsMenuOpen(false);
                }}>

                  <FontAwesomeIcon icon={isDarkMode ? faMoon : faSun} className="w-5 h-5" />
                  <span>{isDarkMode ? t('header.menu.dark_mode') : t('header.menu.light_mode')}</span>
                </button>

                {/* Language Switcher Button */}
                <button
                className="w-full bg-[#FFFFFF] bg-opacity-20 text-[#4C4C4C] text-[18px] font-inter font-medium leading-[25px] rounded-[10px] py-[10px] px-[34px] hover:bg-[#c9c9c9] transition-colors duration-200 flex items-center justify-center gap-[8px]"
                onClick={() => {
                  // Language switching logic will be added later
                  console.log('Language switcher clicked');
                }}>
                  {/* Rectangular flags will be added here */}
                  <div className="flex items-center gap-[12px]">
                    {/* Czech flag placeholder */}
                    <div className="w-[30px] h-[20px] bg-gray-300 rounded-[2px]" title="Czech"></div>
                    {/* Slovak flag placeholder */}
                    <div className="w-[30px] h-[20px] bg-gray-300 rounded-[2px]" title="Slovak"></div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        }
      </div>
    </header>);


});

export default Header;